import { calculateInventoryDataHash } from './inventoryDataHash';
import {
  indexLotsByProduct,
  aggregateLotsMetrics,
  lotProductKey,
  normalizeLotsList
} from './lots/lotsIndex';
import { dedupeLotsList } from '../../utils/lotSystem';

function uniqueField(products, field) {
  return [...new Set((products || []).map((p) => p[field]).filter(Boolean))];
}

/**
 * ADR-0013: Inventory-Overlay-Daten über Stock-Owner (keine parallele Calc-Service-Kette).
 * Linear: index lots once, then O(1) per product.
 */
export function buildCompleteInventoryData(products, lots) {
  const list = Array.isArray(products) ? products : [];
  const cleanLots = dedupeLotsList(normalizeLotsList(lots));
  const lotsByProduct = indexLotsByProduct(cleanLots);
  const inventoryBase = list.map((product) => {
    const stock = aggregateLotsMetrics(lotsByProduct.get(lotProductKey(product.id)) || []);
    const qty = Number(stock.stockNow || 0);
    return {
      id: product.id,
      name: product.name,
      sku: product.sku,
      category: product.category,
      subcategory: product.subcategory,
      type: product.type,
      unit: product.unit,
      calculatedQuantity: qty,
      calculatedValue: Number(stock.value || 0),
      calculatedProfit: Number(stock.potentialProfit || 0),
      lotsCount: stock.lotsCount || 0,
      activeLotsCount: stock.activeLotsCount || 0,
      hasLots: qty > 0,
      lastUpdated: new Date().toISOString(),
      source: 'domain_inventory'
    };
  });

  // Totals from ALL lots (SSOT) — not sum(product.value) and not product-linked-only
  const allLotMetrics = aggregateLotsMetrics(cleanLots);
  const totalValue = Number(allLotMetrics.value || 0);
  const potentialProfit = Number(allLotMetrics.potentialProfit || 0);
  const productsWithStock = inventoryBase.filter((p) => (p.calculatedQuantity || 0) > 0).length;

  return {
    inventoryBase,
    inventoryValue: {
      totalValue,
      potentialProfit,
      byLocation: {},
      byCategory: {},
      lastUpdated: new Date().toISOString(),
      source: 'domain_inventory_lots'
    },
    categories: uniqueField(list, 'category'),
    subcategories: uniqueField(list, 'subcategory'),
    types: uniqueField(list, 'type'),
    statistics: {
      totalProducts: list.length,
      productsWithStock,
      productsWithoutStock: list.length - productsWithStock,
      totalValue,
      calculationTime: 0
    },
    isCalculating: false,
    lastCalculated: new Date().toISOString()
  };
}

export function buildInventoryFallbackData(products, lots, purchases, sales) {
  const list = Array.isArray(products) ? products : [];
  const fallbackHash = calculateInventoryDataHash(list, lots, purchases, sales);
  const lotsArr = normalizeLotsList(lots);
  const lotMetrics = aggregateLotsMetrics(lotsArr);
  const hasLots = lotsArr.length > 0 || (lotMetrics.lotsCount || 0) > 0;

  // Prefer real lots even in fallback — never invent value from stale product.value when lots exist
  if (hasLots) {
    return buildCompleteInventoryData(list, lots);
  }

  return {
    inventoryBase: list.map((product) => ({
      ...product,
      calculatedQuantity: product.quantity || 0,
      calculatedValue: product.value || 0,
      calculatedProfit: 0,
      lotsCount: 0,
      hasLots: false
    })),
    inventoryValue: {
      totalValue: list.reduce((sum, p) => sum + (p.value || 0), 0),
      potentialProfit: 0,
      byLocation: {},
      byCategory: {},
      lastUpdated: new Date().toISOString(),
      source: 'fallback_no_lots'
    },
    categories: uniqueField(list, 'category'),
    subcategories: uniqueField(list, 'subcategory'),
    types: uniqueField(list, 'type'),
    statistics: {
      totalProducts: list.length,
      productsWithStock: list.filter((p) => (p.quantity || 0) > 0).length,
      productsWithoutStock: list.filter((p) => (p.quantity || 0) === 0).length,
      totalValue: list.reduce((sum, p) => sum + (p.value || 0), 0),
      calculationTime: 0
    },
    isCalculating: false,
    lastCalculated: new Date().toISOString(),
    lastDataHash: fallbackHash
  };
}

/**
 * Berechnet Inventardaten ohne React setState.
 * @returns {Promise<{ skipped?: boolean, patch?: object, fallback?: boolean }>}
 */
export async function orchestrateInventoryCalculation({
  products,
  lots,
  purchases,
  sales,
  lastDataHash,
  lastCalculated
}) {
  const currentHash = calculateInventoryDataHash(products, lots, purchases, sales);

  if (lastDataHash === currentHash && lastCalculated) {
    return { skipped: true };
  }

  try {
    const calculatedData = buildCompleteInventoryData(products, lots);
    return {
      patch: {
        ...calculatedData,
        isCalculating: false,
        lastCalculated: new Date().toISOString(),
        lastDataHash: currentHash
      }
    };
  } catch {
    return {
      patch: buildInventoryFallbackData(products, lots, purchases, sales),
      fallback: true
    };
  }
}

export function applyInventoryDataPatch(setInventoryData, patch) {
  if (!patch) return;
  setInventoryData((prev) => ({ ...prev, ...patch }));
}
